/**
 * Marketing media assets (videos + screenshots).
 *
 * Single source of truth for every video/image slot on the marketing site.
 * Each slot ships with `src: null` and renders a styled placeholder (correct
 * aspect ratio + alt text) via `MediaSlot`. To go live with a real asset, drop
 * the file in `public/marketing/` and set `src` to its path here — the real
 * asset renders with zero layout shift. No JSX changes, no broken images.
 */

export type MarketingAssetKey =
  | 'heroDemoVideo'
  | 'prReviewVideo'
  | 'gauntletVideo'
  | 'screenshotPrComment'
  | 'screenshotCheckRun'
  | 'screenshotAnnotation'
  | 'screenshotReviewFindings'
  | 'screenshotFlakyTestFix'
  | 'screenshotReviewVerified';

export interface MarketingAsset {
  /** Path under `public/` (e.g. `/marketing/hero-demo.mp4`), or null until recorded. */
  src: string | null;
  kind: 'video' | 'image';
  /** Placeholder caption shown while `src` is null. */
  label: string;
  /** Accessible description (alt / aria-label) — used for both placeholder and real asset. */
  alt: string;
  /** CSS aspect-ratio (e.g. '16 / 9') so the slot reserves space and never shifts layout. */
  aspect: string;
  /** CSS object-position for choosing which edge remains visible when an image is cropped. */
  objectPosition?: 'top' | 'center' | 'bottom';
}

export const marketingAssets: Record<MarketingAssetKey, MarketingAsset> = {
  heroDemoVideo: {
    src: '/marketing/hero-demo.mp4',
    kind: 'video',
    label: 'The stop hook auto-fix loop',
    alt: "Mindrealm's stop hook reviews an agent's code, flags two real findings, and the agent auto-fixes them until the loop closes with zero findings left",
    aspect: '16 / 10',
  },
  prReviewVideo: {
    src: null,
    kind: 'video',
    label: 'Demo coming soon: a pull request reviewed end to end',
    alt: 'Mindrealm reviewing a pull request and posting a check run with inline annotations',
    aspect: '16 / 9',
  },
  gauntletVideo: {
    src: null,
    kind: 'video',
    label: 'Demo coming soon: real findings in a large open-source repo',
    alt: 'Mindrealm running over a large open-source codebase and surfacing real findings',
    aspect: '16 / 9',
  },
  screenshotPrComment: {
    src: null,
    kind: 'image',
    label: 'Demo coming soon: the pull request summary',
    alt: 'Mindrealm posting a summary on a pull request — what to fix, grouped by severity',
    aspect: '1400 / 900',
  },
  screenshotCheckRun: {
    src: null,
    kind: 'image',
    label: 'Demo coming soon: the Mindrealm Review check run',
    alt: 'The Mindrealm Review check run on a pull request, with a count of blocking and must-fix findings and a pass or fail conclusion',
    aspect: '1400 / 760',
  },
  screenshotAnnotation: {
    src: '/marketing/annotation-error-swallowing.webp',
    kind: 'image',
    label: 'Inline annotation on a pull request',
    alt: 'Mindrealm flagging a swallowed error inline on a GitHub pull request, with a fix the agent can apply',
    aspect: '870 / 282',
  },
  screenshotReviewFindings: {
    src: '/marketing/stop-hook-findings.webp',
    kind: 'image',
    label: 'Five findings, caught before the pull request',
    alt: 'The Mindrealm stop hook blocking an agent after finding five concrete code problems',
    aspect: '1707 / 554',
    objectPosition: 'top',
  },
  screenshotFlakyTestFix: {
    src: '/marketing/flaky-test-fix.webp',
    kind: 'image',
    label: 'A hanging test, fixed with a timeout',
    alt: 'Mindrealm flagging a test that could hang forever and the agent adding a timeout',
    aspect: '1707 / 554',
    objectPosition: 'center',
  },
  screenshotReviewVerified: {
    src: '/marketing/review-verification-passed.webp',
    kind: 'image',
    label: 'Five fixes, verification passed',
    alt: 'The agent summarizing five completed fixes after Mindrealm verification and the test suite pass',
    aspect: '1707 / 554',
    objectPosition: 'center',
  },
};
